import { Alert, Grid, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { FormSelectInputDisplay } from '../../form-field-components/new-and-edit/form-select-input'
import { SelectableCard } from '../../settings-components/data-import/selectable-card'
import { IReportPeriod } from '../../../../shared/services/report-period/report-period.model'
import { CrpPlanType, CrpRegion, CrpScope3Coverage } from '../../../../shared/services/crp-report/crp-report.model'

/**
 * Step 1 — what the plan reports on. A plan's periods are fixed once it has been generated, so this
 * step is read-only after generation rather than silently rewriting a published document's scope.
 */
export const CrpStepPeriods: FunctionComponent<{
  reportPeriods: IReportPeriod[]
  /**
   * Baseline candidates: real report periods plus archived (manually entered) base years, from the
   * combined endpoint. The current reporting year keeps using reportPeriods only.
   */
  baselinePeriods: any[]
  baselineReportPeriodId: string
  currentReportPeriodId: string
  region: CrpRegion
  scope3Coverage: CrpScope3Coverage
  planType: CrpPlanType
  locked: boolean
  onChange: (field: string, value: string) => void
}> = ({
  reportPeriods,
  baselinePeriods,
  baselineReportPeriodId,
  currentReportPeriodId,
  region,
  scope3Coverage,
  planType,
  locked,
  onChange,
}) => {
  const periodOptions = reportPeriods.map((period) => ({
    id: period.id,
    name: period.friendlyName || String(period.calendarYear),
  }))
  const baselineOptions = baselinePeriods.map((period: any) => {
    const qualifiers = [
      ...(period.baseYear ? ['base year'] : []),
      ...(period.archived ? ['entered manually'] : []),
    ]
    return {
      id: period.id,
      name: `${period.friendlyName || String(period.calendarYear)}${qualifiers.length ? ` (${qualifiers.join(', ')})` : ''}`,
    }
  })
  // Ids from the two tables can never collide, so the guard also compares calendar years: an
  // archived 2027 baseline against the real 2027 reporting period is still the same year.
  const baselineYear = baselinePeriods.find((period: any) => period.id === baselineReportPeriodId)?.calendarYear
  const currentYear = reportPeriods.find((period) => period.id === currentReportPeriodId)?.calendarYear
  const sameYearSelected = Boolean(
    baselineReportPeriodId && currentReportPeriodId && (
      baselineReportPeriodId === currentReportPeriodId
      || (baselineYear != null && currentYear != null && String(baselineYear) === String(currentYear))
    ),
  )

  return (
    <>
      <Typography variant='h6' sx={{ mb: 1 }}>Reporting years and basis</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 3 }}>
        A Carbon Reduction Plan compares a fixed baseline year against your current reporting year.
      </Typography>

      {locked && (
        <Alert severity='info' sx={{ mb: 3 }}>
          This plan has already been generated, so the years and basis are fixed. Create a new plan to report
          on different years.
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <SelectableCard
            title='Carbon Reduction Plan'
            description='The PPN 006 Annex A plan required to bid for NHS and UK public-sector contracts.'
            align='left'
            fullHeight
            selected={planType === 'CRP'}
            disabled={locked}
            onClick={() => onChange('planType', 'CRP')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectableCard
            title='Progress report'
            description='Reports every year since your baseline against your targets. Published progress reporting is required from 2030.'
            align='left'
            fullHeight
            selected={planType === 'PROGRESS'}
            disabled={locked}
            onClick={() => onChange('planType', 'PROGRESS')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <FormSelectInputDisplay
            isEditPage={false}
            disabled={locked}
            title='Baseline year'
            name='baselineReportPeriodId'
            value={baselineReportPeriodId}
            onChange={(event: any) => onChange('baselineReportPeriodId', event.target.value)}
            array={baselineOptions}
            valueKey='id'
            labelKey='name'
            forceOutlined
            isTopRow
            isBottomRow
            tooltip='The fixed reference year your reductions are measured against. Defaults to the reporting period marked as your base year.'
          />
        </Grid>
        {planType === 'CRP' && (
          <Grid item xs={12} md={6}>
            <FormSelectInputDisplay
              isEditPage={false}
              disabled={locked}
              title='Current reporting year'
              name='currentReportPeriodId'
              value={currentReportPeriodId}
              onChange={(event: any) => onChange('currentReportPeriodId', event.target.value)}
              array={periodOptions}
              valueKey='id'
              labelKey='name'
              forceOutlined
              isTopRow
              isBottomRow
              customErrorMessage={sameYearSelected ? 'Choose a different year from the baseline' : undefined}
            />
          </Grid>
        )}
      </Grid>

      {sameYearSelected && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          The baseline and reporting years must be different.
        </Alert>
      )}

      <Typography variant='subtitle1' sx={{ mb: 1 }}>Emissions basis</Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
        PPN 006 requires UK Scope 1 and 2 emissions plus the required Scope 3 categories.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <SelectableCard
            title='UK operations'
            description='The PPN 006 basis. Includes only emissions recorded against the United Kingdom.'
            align='left'
            fullHeight
            selected={region === 'uk'}
            disabled={locked}
            onClick={() => onChange('region', 'uk')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectableCard
            title='Global operations'
            description='All emissions wherever they occur. Voluntary today; this becomes the required basis in April 2027.'
            align='left'
            fullHeight
            selected={region === 'global'}
            disabled={locked}
            onClick={() => onChange('region', 'global')}
          />
        </Grid>
      </Grid>

      <Typography variant='subtitle1' sx={{ mb: 1 }}>Scope 3 coverage</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <SelectableCard
            title='PPN 006 minimum (5 categories)'
            description='Business travel, employee commuting, upstream and downstream transportation and distribution, and waste generated in operations.'
            align='left'
            fullHeight
            selected={scope3Coverage === 'PPN_MINIMUM'}
            disabled={locked}
            onClick={() => onChange('scope3Coverage', 'PPN_MINIMUM')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectableCard
            title='All 15 GHG Protocol categories'
            description='Reports every Scope 3 category you record. Exceeds the current requirement and matches the April 2027 basis.'
            align='left'
            fullHeight
            selected={scope3Coverage === 'ALL_CATEGORIES'}
            disabled={locked}
            onClick={() => onChange('scope3Coverage', 'ALL_CATEGORIES')}
          />
        </Grid>
      </Grid>
    </>
  )
}
